/**
 * Deterministic scoring for the "Which AI Coding Tool Should I Use?" quiz
 * ({@link ../components/tools/ai-coding-tool-quiz.tsx}).
 *
 * SINGLE SOURCE OF TRUTH for the question/answer structure and the weighted
 * scoring map, so the widget and the unit tests
 * ({@link ./coding-tool-quiz.test.ts}) score identically. Pure and fully
 * deterministic: the same answers always yield the same recommendation, with a
 * fixed tie-break order. No randomness, no I/O, SSR-safe.
 *
 * The six recommendable tools map to real comparison/hub pages so the result
 * can deep-link the reader to honest, current detail. Localized labels and copy
 * live in the dictionary; this module only carries stable ids, weights and the
 * locale-independent link targets. No em dashes anywhere.
 */

/** The tools the quiz can recommend. Stable ids, used as scoring keys. */
export type ToolId =
  | 'claude-code'
  | 'cursor'
  | 'codex-cli'
  | 'github-copilot'
  | 'windsurf'
  | 'aider'

export const TOOL_IDS: ToolId[] = [
  'claude-code',
  'cursor',
  'codex-cli',
  'github-copilot',
  'windsurf',
  'aider',
]

/**
 * Deterministic tie-break order. When two tools share the top score, the one
 * earlier in this list wins. Claude Code leads (this is a Claude-focused
 * academy and it is the strongest all-round agent in 2026), then the other
 * tools in rough breadth-of-fit order. This makes every result reproducible.
 */
const TIE_BREAK_ORDER: ToolId[] = [
  'claude-code',
  'cursor',
  'codex-cli',
  'windsurf',
  'github-copilot',
  'aider',
]

/** A single answer option: its stable id and the points it adds per tool. */
export type QuizOption = {
  /** Stable, locale-independent id (used as the React value + in tests). */
  id: string
  /** Points this option contributes to each tool. Omitted tools get 0. */
  weights: Partial<Record<ToolId, number>>
}

/** A single question: a stable id and its ordered answer options. */
export type QuizQuestion = {
  /** Stable, locale-independent id; the dictionary keys its copy off this. */
  id: string
  options: QuizOption[]
}

/**
 * The seven quiz questions and their weighted answers. Weights are small
 * integers; the recommendation is the tool with the highest summed score.
 *
 * Design notes on the weighting:
 *   - "Terminal vs IDE" is the single biggest signal, so it carries the
 *     heaviest weights (terminal -> Claude Code / Codex CLI / Aider, IDE ->
 *     Cursor / Windsurf / Copilot).
 *   - Budget steers toward open-source (Aider) or the cheaper assistant
 *     (Copilot) at the low end and the frontier agents at the high end.
 *   - Autonomy, codebase size, primary use and experience refine the pick.
 */
export const QUIZ_QUESTIONS: QuizQuestion[] = [
  {
    id: 'environment',
    options: [
      {
        id: 'terminal',
        weights: {
          'claude-code': 3,
          'codex-cli': 3,
          aider: 2,
        },
      },
      {
        id: 'ide',
        weights: {
          cursor: 3,
          windsurf: 3,
          'github-copilot': 2,
        },
      },
      {
        id: 'either',
        weights: {
          'claude-code': 1,
          cursor: 1,
        },
      },
    ],
  },
  {
    id: 'autonomy',
    options: [
      {
        id: 'autonomous',
        weights: {
          'claude-code': 3,
          'codex-cli': 2,
          cursor: 1,
        },
      },
      {
        id: 'collaborative',
        weights: {
          cursor: 2,
          windsurf: 2,
          'claude-code': 1,
        },
      },
      {
        id: 'autocomplete',
        weights: {
          'github-copilot': 3,
          cursor: 1,
        },
      },
    ],
  },
  {
    id: 'experience',
    options: [
      {
        id: 'beginner',
        weights: {
          cursor: 3,
          'github-copilot': 2,
          windsurf: 1,
        },
      },
      {
        id: 'intermediate',
        weights: {
          cursor: 2,
          'claude-code': 2,
          windsurf: 1,
        },
      },
      {
        id: 'advanced',
        weights: {
          'claude-code': 3,
          'codex-cli': 2,
          aider: 2,
        },
      },
    ],
  },
  {
    id: 'budget',
    options: [
      {
        id: 'free',
        weights: {
          aider: 3,
          'github-copilot': 1,
        },
      },
      {
        id: 'value',
        weights: {
          'github-copilot': 2,
          cursor: 1,
          'codex-cli': 1,
        },
      },
      {
        id: 'premium',
        weights: {
          'claude-code': 2,
          cursor: 1,
        },
      },
    ],
  },
  {
    id: 'codebase',
    options: [
      {
        id: 'small',
        weights: {
          'github-copilot': 2,
          cursor: 1,
          aider: 1,
        },
      },
      {
        id: 'medium',
        weights: {
          cursor: 2,
          'claude-code': 1,
          windsurf: 1,
        },
      },
      {
        id: 'large',
        weights: {
          'claude-code': 3,
          windsurf: 2,
          'codex-cli': 1,
        },
      },
    ],
  },
  {
    id: 'useCase',
    options: [
      {
        id: 'features',
        weights: {
          'claude-code': 2,
          cursor: 2,
        },
      },
      {
        id: 'refactor',
        weights: {
          'claude-code': 3,
          aider: 1,
        },
      },
      {
        id: 'learning',
        weights: {
          cursor: 2,
          'github-copilot': 2,
        },
      },
      {
        id: 'scripts',
        weights: {
          'codex-cli': 2,
          aider: 2,
        },
      },
    ],
  },
  {
    id: 'openSource',
    options: [
      {
        id: 'must',
        weights: {
          aider: 3,
        },
      },
      {
        id: 'prefer',
        weights: {
          aider: 1,
          'codex-cli': 1,
        },
      },
      {
        id: 'dontcare',
        weights: {
          'claude-code': 1,
          cursor: 1,
        },
      },
    ],
  },
]

/**
 * Where each recommended tool deep-links to. Each tool points at the most
 * relevant comparison (or the best-X hub) so the reader can read the honest
 * detail behind the pick. Slugs are locale-independent comparison slugs the
 * widget resolves to a localized /compare URL.
 */
export const TOOL_LINK: Record<ToolId, { comparisonSlug: string }> = {
  'claude-code': { comparisonSlug: 'claude-code-vs-cursor' },
  cursor: { comparisonSlug: 'claude-code-vs-cursor' },
  'codex-cli': { comparisonSlug: 'claude-code-vs-codex-cli' },
  'github-copilot': { comparisonSlug: 'cursor-vs-windsurf-vs-copilot' },
  windsurf: { comparisonSlug: 'cursor-vs-windsurf-vs-copilot' },
  aider: { comparisonSlug: 'best-ai-coding-tools' },
}

/** The scored outcome. `scores` is every tool's total for transparency/tests. */
export type QuizResult = {
  /** The recommended tool id (highest score, deterministic tie-break). */
  winner: ToolId
  /** Every tool's summed score, for the "how it scored" breakdown. */
  scores: Record<ToolId, number>
}

/** Build a zeroed score map. */
function zeroScores(): Record<ToolId, number> {
  return {
    'claude-code': 0,
    cursor: 0,
    'codex-cli': 0,
    'github-copilot': 0,
    windsurf: 0,
    aider: 0,
  }
}

/**
 * Look up the chosen option for a question by id. Returns undefined when the
 * question was not answered or the id is unknown.
 */
function findOption(
  question: QuizQuestion,
  optionId: string | undefined,
): QuizOption | undefined {
  if (optionId === undefined) return undefined
  return question.options.find((option) => option.id === optionId)
}

/**
 * Score a full set of answers. `answers` maps a question id to the chosen
 * option id; unanswered questions simply contribute nothing.
 *
 * Pure and deterministic. The winner is the highest total; ties are broken by
 * {@link TIE_BREAK_ORDER}. When nothing has been answered, the winner falls back
 * to the first tie-break entry (Claude Code) so callers always get a valid id,
 * but the widget only shows a result once every question is answered.
 */
export function scoreQuiz(answers: Record<string, string>): QuizResult {
  const scores = zeroScores()

  for (const question of QUIZ_QUESTIONS) {
    const option = findOption(question, answers[question.id])
    if (!option) continue
    for (const tool of TOOL_IDS) {
      const weight = option.weights[tool]
      if (weight) scores[tool] += weight
    }
  }

  let winner: ToolId = TIE_BREAK_ORDER[0]
  let best = -Infinity
  for (const tool of TIE_BREAK_ORDER) {
    if (scores[tool] > best) {
      best = scores[tool]
      winner = tool
    }
  }

  return { winner, scores }
}

/** Whether every quiz question has an answer. Drives the "see result" gate. */
export function isComplete(answers: Record<string, string>): boolean {
  return QUIZ_QUESTIONS.every((question) => {
    const optionId = answers[question.id]
    return findOption(question, optionId) !== undefined
  })
}
